import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  Typography,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { listPatients, PatientListItem } from "../api/patients";
import { formatLocalDateTime } from "../utils/datetime";

// Physician: список своих пациентов (English production UI).
// Backend сам фильтрует по команде врача.

const SEX_SHORT: Record<string, string> = { male: "M", female: "F" };

const EDUCATION_SHORT: Record<string, string> = {
  incompleteSecondaryEducation: "Incompl. sec.",
  secondaryEducation: "Secondary",
  incompleteHigherEducation: "Incompl. higher",
  higherEducation: "Higher",
  academicDegree: "Academic degree",
};

type DateFilter = "all" | "today" | "yesterday" | "custom";

function getDateRange(filter: DateFilter, customFrom?: string, customTo?: string): { from: Date | null; to: Date | null } {
  const now = new Date();
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

  switch (filter) {
    case "today":
      return { from: startOfDay(now), to: now };
    case "yesterday": {
      const yesterday = new Date(now);
      yesterday.setDate(yesterday.getDate() - 1);
      return { from: startOfDay(yesterday), to: startOfDay(now) };
    }
    case "custom": {
      const from = customFrom ? new Date(customFrom + "T00:00:00") : null;
      const to = customTo ? new Date(customTo + "T23:59:59") : null;
      return { from, to };
    }
    default:
      return { from: null, to: null };
  }
}

export default function PhysicianPatientsPage() {
  const navigate = useNavigate();
  const [patients, setPatients] = useState<PatientListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dateFilter, setDateFilter] = useState<DateFilter>("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const fetchPatients = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listPatients({ skip: 0, limit: 100, search: search || undefined });
      setPatients(res.items);
      setTotal(res.total);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  const filteredAndSorted = useMemo(() => {
    let list = [...patients];

    list.sort((a, b) => {
      const da = a.created_at ? new Date(a.created_at).getTime() : 0;
      const db = b.created_at ? new Date(b.created_at).getTime() : 0;
      return db - da;
    });

    if (dateFilter !== "all") {
      const { from, to } = getDateRange(dateFilter, customFrom, customTo);
      list = list.filter((p) => {
        if (!p.created_at) return false;
        const d = new Date(p.created_at);
        if (from && d < from) return false;
        if (to && d > to) return false;
        return true;
      });
    }

    return list;
  }, [patients, dateFilter, customFrom, customTo]);

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1.5 }}>
        My Patients ({total})
      </Typography>

      <TextField
        fullWidth
        placeholder="Search by patient ID"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        size="small"
        sx={{ mb: 1.5 }}
      />

      <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: "wrap", gap: 0.5 }}>
        <ToggleButtonGroup
          value={dateFilter}
          exclusive
          onChange={(_, val) => { if (val !== null) setDateFilter(val); }}
          size="small"
        >
          <ToggleButton value="all" sx={{ textTransform: "none", px: 2, py: 0.5, fontSize: 13 }}>All</ToggleButton>
          <ToggleButton value="today" sx={{ textTransform: "none", px: 2, py: 0.5, fontSize: 13 }}>Today</ToggleButton>
          <ToggleButton value="yesterday" sx={{ textTransform: "none", px: 2, py: 0.5, fontSize: 13 }}>Yesterday</ToggleButton>
          <ToggleButton value="custom" sx={{ textTransform: "none", px: 2, py: 0.5, fontSize: 13 }}>Range</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {dateFilter === "custom" && (
        <Stack direction="row" spacing={1.5} sx={{ mb: 1.5 }}>
          <TextField
            type="date"
            label="From"
            value={customFrom}
            onChange={(e) => setCustomFrom(e.target.value)}
            size="small"
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            type="date"
            label="To"
            value={customTo}
            onChange={(e) => setCustomTo(e.target.value)}
            size="small"
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Stack>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : filteredAndSorted.length === 0 ? (
        <Typography sx={{ color: "text.secondary", textAlign: "center", py: 4 }}>
          No patients found
        </Typography>
      ) : (
        <Paper variant="outlined">
          <List disablePadding>
            {filteredAndSorted.map((p, i) => (
              <ListItemButton
                key={p.patient_id || p.id}
                divider={i < filteredAndSorted.length - 1}
                onClick={() => navigate(`/physician/patient/${p.patient_id}`)}
                sx={{ py: 1, px: 2 }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ display: "inline-block", px: 1, py: 0.3, bgcolor: "#e3f2fd", borderRadius: 1, minWidth: 90, textAlign: "center" }}>
                        <Typography sx={{ fontFamily: "monospace", fontWeight: 700, fontSize: 14, letterSpacing: 1 }}>
                          {p.patient_id || "—"}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={0.5}>
                        {p.sex && <Chip label={SEX_SHORT[p.sex]} size="small" variant="outlined" sx={{ height: 22, fontSize: 12 }} />}
                        {p.age != null && <Chip label={p.age} size="small" variant="outlined" sx={{ height: 22, fontSize: 12 }} />}
                        {p.education && <Chip label={EDUCATION_SHORT[p.education] || p.education} size="small" variant="outlined" sx={{ height: 22, fontSize: 12 }} />}
                      </Stack>
                    </Box>
                  }
                  secondary={
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      {p.created_at ? formatLocalDateTime(p.created_at) : ""}
                      {p.scan_count > 0 ? ` · ${p.scan_count} test${p.scan_count > 1 ? "s" : ""}` : ""}
                    </Typography>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
}
